import { Injectable, signal } from "@angular/core";
import { Message, MessageType } from "./message";
import { sleep } from "../../../util/sleep.util";
import { ButtonAction, ErrorObject } from "../../../services/base-http.service";
import { ErrorMessage } from "./error-modal/error-message";
import { Observable, Subject, Subscription } from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class MessageService {

  // Signals with immutable updates, so OnPush components showing these messages get notified
  // (the messages are changed from anywhere in the app, and after timeouts).
  private readonly messageState = signal<Message>(new Message());
  private readonly errorMessageState = signal<ErrorMessage>(new ErrorMessage());

  readonly currentMessage = this.messageState.asReadonly();
  readonly currentErrorMessage = this.errorMessageState.asReadonly();

  get message(): Message {
    return this.messageState();
  }

  get errorMessage(): ErrorMessage {
    return this.errorMessageState();
  }

  updateObservable: Observable<ButtonAction>;
  private updateSubject: Subject<ButtonAction> = new Subject();

  private hideTimeoutId: any;
  private snoozing = false;

  constructor() {
    this.updateObservable = this.updateSubject.asObservable();
  }

  subscribeToUpdates(next: (buttonAction: ButtonAction) => void): Subscription {
    return this.updateObservable.subscribe(next);
  }

  triggerUpdate(buttonAction: ButtonAction) {
    this.updateSubject.next(buttonAction);
  }

  showSuccessMessage(message: string): void {
    this.showMessage("SUCCESS", message);
  }

  showInfoMessage(message: string): void {
    this.showMessage("INFO", message);
  }

  showWarningMessage(message: string): void {
    this.showMessage("WARN", message);
  }

  async showErrorMessage(message: string) {
    this.showMessage("ERROR", message);
  }

  showErrorModal(errorObject: ErrorObject): void {
    this.errorMessageState.set({ errorObject, hide: false, active: true });
  }

  hasActiveStickyMessage(): boolean {
    return this.message.active &&
        (this.message.type === "WARN" || this.message.type === "ERROR");
  }

  snooze(): void {
    this.snoozing = true;
  }

  stopSnoozing(): void {
    this.snoozing = false;
  }

  snoozed(): boolean {
    return this.snoozing;
  }

  async hideMessage() {
    if (this.message.active) {
      this.messageState.update(message => ({ ...message, hide: true }));
      // delay, so the opacity transform is shown (see duration-300 in message.component.html)
      await sleep(300);
      this.messageState.update(message => ({ ...message, active: false }));
    }
  }

  async hideErrorMessage() {
    if (this.errorMessage.active) {
      this.errorMessageState.update(errorMessage => ({ ...errorMessage, hide: true }));
      // delay, so the opacity transform is shown (see duration-300 in message.component.html)
      await sleep(300);
      this.errorMessageState.update(errorMessage => ({ ...errorMessage, active: false }));
    }
  }

  actionButtonClicked(buttonAction: ButtonAction) {
    this.triggerUpdate(buttonAction);
  }

  private showMessage(type: MessageType, message: string): void {
    if (!this.snoozing) {
      clearTimeout(this.hideTimeoutId);
      this.messageState.set({ message, type, hide: false, active: true });
      if (type === "SUCCESS" || type === "INFO") {
        const duration = Math.min(2000, 1000 + (this.countWords(message) * 300));
        this.hideTimeoutId = setTimeout(() => this.hideMessage(), duration);
      }
    }
  }

  private countWords(message: string): number {
    return message.split(/\W+/).length;
  }
}
